// =============================================================================
// SaasAdminLogin — the platform admin console login.
//
// Split screen: a dark product panel on the left, the sign-in card on the right.
// Deliberately darker than the owner-facing /signup screen — this is an internal
// console, and looking different from the customer login is a feature: an admin
// should never be unsure which door they are standing at.
//
// WHAT IS NOT HERE, AND WHY (from the reference design):
//   • "Sign in with Google" — there is no Google OAuth in this backend. The only
//     admin credential path is POST /saas/admin/login (bcrypt over `users` where
//     account_type='admin'). A button that cannot work is worse than no button.
//   • "Create an admin account" — admin self-signup does not exist by design;
//     admins are provisioned directly. That link would be a dead end.
//   • "Remember me" — no admin-side backend for a long-lived session.
// Add each back the day its backend lands, not before.
//
// "Forgot password?" IS here now (BUG-25): POST /saas/admin/password/forgot
// issues the same single-use reset link a super admin's "Send password reset"
// does, redeemed on /admin/accept/:token. The reply is identical whether or not
// the email is an admin, and this screen shows it verbatim — it must never
// phrase success as "we found your account".
//
// The auth wiring below is UNCHANGED from the previous version: same API call,
// same redux dispatch, same error handling. Only the presentation is new.
// =============================================================================
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { ArrowLeft } from "lucide-react";
import { login } from "../../redux/reducers/authSlice";
import { saasAdminLogin, saasAdminForgotPassword } from "../../redux/saas-reducers/saasAdminApis";
import "./saas-admin.css";

/* -- inline icons (SVG, never emoji — they inherit currentColor) ------------ */
const Ico = ({ d, size = 18, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>
    <path d={d} />
  </svg>
);
const P = {
  shield: "M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z",
  users: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8M22 21v-2a4 4 0 0 0-3-3.87",
  chart: "M4 20V10M10 20V4M16 20v-7M22 20H2",
  card: "M2 7h20M2 7v10a2 2 0 002 2h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2zM6 15h4",
  lock: "M5 11h14v10H5zM8 11V7a4 4 0 118 0v4",
  key: "M15 7a4 4 0 11-3.9 5H7v3H4v-3l3.1-3H11A4 4 0 0115 7z",
  mail: "M3 6h18v12H3zM3 7l9 6 9-6",
  eye: "M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12zM12 15a3 3 0 100-6 3 3 0 000 6z",
  eyeOff: "M3 3l18 18M10.6 10.6a3 3 0 004.2 4.2M9.9 5.2A10.9 10.9 0 0112 5c7 0 11 7 11 7a18.6 18.6 0 01-3.4 4.2M6.2 6.7A18.4 18.4 0 001 12s4 7 11 7c1 0 2-.1 2.8-.4",
  arrow: "M5 12h14M13 6l6 6-6 6",
  db: "M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3zM4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6",
  alert: "M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z",
  check: "M20 6L9 17l-5-5",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// BUG-25 — self-service recovery. Swaps in for the sign-in form inside the same
// card, so there is no extra route to guard and "Back to sign in" is one click.
const ForgotForm = ({ initialEmail, onBack }) => {
  const [email, setEmail] = useState(EMAIL_RE.test(initialEmail || "") ? initialEmail : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!EMAIL_RE.test(v)) { setError("Enter the email address on your admin account."); return; }
    setBusy(true); setError("");
    try {
      const r = await saasAdminForgotPassword(v);
      if (r && r.status) setDone(r.message || "If that email belongs to an active admin account, a reset link is on its way.");
      else setError((r && r.message) || "Could not send the reset link. Please try again.");
    } catch (e2) {
      setError("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <>
      <p className="sadm-auth__sub">Enter your admin email and we'll send a one-time link to choose a new password.</p>
      {error && (
        <div className="sadm-auth__err" role="alert">
          <Ico d={P.alert} size={16} /> <span>{error}</span>
        </div>
      )}
      {done ? (
        <div className="sadm-auth__ok" role="status">
          <Ico d={P.check} size={16} /> <span>{done}</span>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <div className="sadm-auth__field">
            <label htmlFor="sadm-forgot">Admin email</label>
            <div className="sadm-auth__wrap">
              <span className="sadm-auth__lic" aria-hidden="true"><Ico d={P.mail} size={17} /></span>
              <input
                id="sadm-forgot" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@wtfgyms.com" autoComplete="username" autoFocus
              />
            </div>
          </div>
          <button className="sadm-auth__cta" type="submit" disabled={busy}>
            {busy ? "Sending…" : <>Send reset link <Ico d={P.arrow} size={17} /></>}
          </button>
        </form>
      )}
      <div className="sadm-auth__back">
        <button type="button" className="sadm-auth__link" onClick={onBack}><ArrowLeft size={14} aria-hidden="true" /> Back to sign in</button>
      </div>
    </>
  );
};

const FEATURES = [
  { icon: P.users, label: "Owners, gyms & subscriptions" },
  { icon: P.chart, label: "Revenue, invoices & transactions" },
  { icon: P.card, label: "Plans, pricing & feature monetization" },
  { icon: P.key,   label: "Role & access control" },
  { icon: P.shield, label: "Tenant-scoped by design" },
];

// Short, defensible security notes. Deliberately NOT "99.9% uptime" or "24/7
// support" — we publish no SLA and make no support-hours commitment, and the
// same claim was removed from /pricing for that reason.
const NOTES = [
  { icon: P.shield, title: "Tenant-scoped", body: "Every query is scoped to its tenant" },
  { icon: P.lock,   title: "Encrypted at rest", body: "KYC data and secrets" },
  { icon: P.db,     title: "Admin-only", body: "Separate credential path" },
];

/* -- the product mockup: pure CSS/SVG, zero image assets -------------------- */
// Illustrative UI, not data. No personal names appear here — the platform's own
// rule is that we publish types, never instances.
const Mockup = () => (
  <div className="sadm-mock" aria-hidden="true">
    <div className="sadm-mock__win">
      <div className="sadm-mock__bar">
        <span className="sadm-mock__mk">W</span>
        <span className="sadm-mock__t">Dashboard</span>
        <span className="sadm-mock__dots"><i /><i /><i /></span>
      </div>
      <div className="sadm-mock__tiles">
        {[["Owners", "1,284"], ["Active gyms", "182"], ["MRR", "₹48,250"]].map(([k, v]) => (
          <div className="sadm-mock__tile" key={k}>
            <span>{k}</span><b>{v}</b>
            <span className="sadm-mock__spark" />
          </div>
        ))}
      </div>
      <div className="sadm-mock__chart">
        <span className="sadm-mock__ct">Revenue overview</span>
        <svg viewBox="0 0 260 76" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="sadmFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#7c5cff" stopOpacity=".45" />
              <stop offset="1" stopColor="#7c5cff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0 60 L36 52 L72 56 L108 38 L144 44 L180 26 L216 18 L260 8 L260 76 L0 76 Z" fill="url(#sadmFill)" />
          <path d="M0 60 L36 52 L72 56 L108 38 L144 44 L180 26 L216 18 L260 8" fill="none" stroke="#a18cff" strokeWidth="2" />
          <circle cx="260" cy="8" r="3.5" fill="#fff" />
        </svg>
      </div>
      <div className="sadm-mock__rows">
        {["New owner onboarded", "Subscription renewed", "Membership sold"].map((r, i) => (
          <div className="sadm-mock__row" key={r}>
            <span className="sadm-mock__dot" style={{ animationDelay: `${i * 0.4}s` }} />
            <span>{r}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="sadm-mock__phone">
      <div className="sadm-mock__notch" />
      <span className="sadm-mock__ph-t">Members</span>
      <div className="sadm-mock__ph-search" />
      {[68, 82, 55, 74].map((w, i) => (
        <div className="sadm-mock__ph-row" key={i}>
          <span className="sadm-mock__av" />
          <span className="sadm-mock__ln" style={{ width: `${w}%` }} />
        </div>
      ))}
    </div>
  </div>
);

const SaasAdminLogin = () => {
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) { setError("Enter your email/mobile and password."); return; }
    setBusy(true); setError("");
    try {
      const r = await saasAdminLogin(username.trim(), password);
      if (r && r.status && r.data && r.data.token) dispatch(login(r.data));
      else setError((r && r.message) || "Login failed.");
    } catch (e2) {
      setError("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="sadm-auth">
      {/* ------------------------------------------------ LEFT (dark) ---- */}
      <aside className="sadm-auth__side">
        <div className="sadm-auth__glow" aria-hidden="true" />
        <div className="sadm-auth__sidein">
          <div className="sadm-auth__lockup">
            <span className="sadm-auth__mk">W</span>
            <span className="sadm-auth__word">WTF<span>GO</span></span>
          </div>

          <span className="sadm-auth__chip">Platform admin console</span>

          <h2 className="sadm-auth__h2">
            Powerful platform.<br /><em>Built for growth.</em>
          </h2>
          <p className="sadm-auth__lead">
            Owners, gyms, subscriptions, invoices and feature pricing — the whole
            platform from one console.
          </p>

          <ul className="sadm-auth__feats">
            {FEATURES.map((f) => (
              <li key={f.label}>
                <span className="sadm-auth__fic"><Ico d={f.icon} size={15} /></span>
                {f.label}
              </li>
            ))}
          </ul>

          <Mockup />
        </div>
      </aside>

      {/* ----------------------------------------------- RIGHT (light) --- */}
      <main className="sadm-auth__main">
        {/* Brand lockup for narrow screens — the dark panel that carries it is
            hidden below 1024, and a login with no identity on it is a login the
            visitor cannot place. Duplicated rather than moved so the desktop
            composition is untouched; aria-hidden because the h1 below already
            names the page for assistive tech. */}
        <div className="sadm-auth__mobrand" aria-hidden="true">
          <span className="sadm-auth__mk">W</span>
          <span className="sadm-auth__word">WTF<span>GO</span></span>
        </div>

        <div className="sadm-auth__card">
          <span className="sadm-auth__badge" aria-hidden="true"><Ico d={P.shield} size={22} /></span>
          <h1 className="sadm-auth__h1">{forgot ? "Reset your password" : "Welcome back, Admin"}</h1>
          {forgot ? (
            <ForgotForm initialEmail={username.trim()} onBack={() => setForgot(false)} />
          ) : (<>
          <p className="sadm-auth__sub">Sign in to the platform admin console.</p>

          {error && (
            <div className="sadm-auth__err" role="alert">
              <Ico d={P.alert} size={16} /> <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} noValidate>
            <div className="sadm-auth__field">
              <label htmlFor="sadm-user">Email or mobile</label>
              <div className="sadm-auth__wrap">
                <span className="sadm-auth__lic" aria-hidden="true"><Ico d={P.mail} size={17} /></span>
                <input
                  id="sadm-user" value={username} onChange={(e) => setUsername(e.target.value)}
                  placeholder="you@wtfgyms.com" autoComplete="username" autoFocus
                />
              </div>
            </div>

            <div className="sadm-auth__field">
              <label htmlFor="sadm-pass">Password</label>
              <div className="sadm-auth__wrap">
                <span className="sadm-auth__lic" aria-hidden="true"><Ico d={P.lock} size={17} /></span>
                <input
                  id="sadm-pass" type={show ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" autoComplete="current-password"
                />
                <button
                  type="button" className="sadm-auth__peek" onClick={() => setShow((s) => !s)}
                  aria-label={show ? "Hide password" : "Show password"} aria-pressed={show}
                >
                  <Ico d={show ? P.eyeOff : P.eye} size={17} />
                </button>
              </div>
            </div>

            <div className="sadm-auth__forgot">
              <button type="button" className="sadm-auth__link" onClick={() => { setError(""); setForgot(true); }}>
                Forgot password?
              </button>
            </div>

            <button className="sadm-auth__cta" type="submit" disabled={busy}>
              {busy ? "Signing in…" : <>Sign in to dashboard <Ico d={P.arrow} size={17} /></>}
            </button>
          </form>
          </>)}

          <div className="sadm-auth__notes">
            {NOTES.map((n) => (
              <div key={n.title}>
                <span className="sadm-auth__nic" aria-hidden="true"><Ico d={n.icon} size={15} /></span>
                <b>{n.title}</b>
                <span>{n.body}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="sadm-auth__foot">
          Admin access is provisioned by the platform team — there is no self-signup.
        </p>
      </main>
    </div>
  );
};

export default SaasAdminLogin;
